/**
 * Hook: useCustomerImport
 *
 * Centraliza chamadas RPC do fluxo de importação em massa de CLIENTES.
 * Espelha a API de useDealImport (mesma forma/contrato) para que os
 * passos do wizard possam ser reutilizados com mínima divergência.
 *
 * Fluxo:
 *   1. createJob          - cria registro draft com mapping/defaults/options
 *   2. populateItems      - envia rows do CSV em lotes (frontend pagina)
 *   3. validateJob        - dry-run server-side, classifica cada item
 *   4. approveAndEnqueue  - aprovação explícita, enfileira batches PGMQ
 *   5. cancelJob          - cancelamento manual
 *   6. deleteJob          - exclusão definitiva
 *   7. useJobDetails      - polling de progresso
 *   8. useJobItems        - lista paginada de itens (filtro por status)
 *   9. useInvalidItems    - prévia paginada de linhas inválidas/dup/blacklist
 *
 * RELEASE: 2026-05-17 v1.0.0 - Versão inicial (Fase 2 importação clientes)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useCustomerImport');

// ============= Tipos =============
export interface CustomerImportMapping {
  [csvColumn: string]: string;
}

export interface CustomerImportDefaults {
  source?: string;
  /** Dedupe único suportado pelo backend é 'skip'. Mantido para futura extensão. */
  duplicate_behavior?: 'skip';
}

export interface CustomerImportOptions {
  silent_mode?: boolean;
  default_country_code?: string;
}

export interface ValidationCounters {
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  blacklisted_count: number;
}

export interface CustomerImportJobDetails {
  job: any;
  batches_summary: {
    total: number;
    pending: number;
    enqueued: number;
    processing: number;
    completed: number;
    failed: number;
    stalled?: number;
  };
  last_batch_update?: string | null;
  last_batch_error?: string | null;
}

// ============= Hook =============
export function useCustomerImport() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const qc = useQueryClient();

  // 1) Criar job
  const createJob = useMutation({
    mutationFn: async (input: {
      name: string;
      source_file_path?: string;
      source_file_name?: string;
      mapping: CustomerImportMapping;
      defaults: CustomerImportDefaults;
      options: CustomerImportOptions;
      total_rows: number;
    }) => {
      const { data, error } = await supabase.rpc('create_customer_import_job', {
        p_name: input.name,
        p_source_file_path: input.source_file_path ?? null,
        p_source_file_name: input.source_file_name ?? null,
        p_mapping: input.mapping as any,
        p_defaults: input.defaults as any,
        p_options: input.options as any,
        p_total_rows: input.total_rows,
        p_tenant_hint: tenantId,
      } as any);
      if (error) throw error;
      return data as unknown as string;
    },
  });

  // 2) Popular itens em chunks de 500
  const populateItems = useMutation({
    mutationFn: async (input: {
      jobId: string;
      rows: Array<{ row_index: number; raw_row: Record<string, unknown> }>;
    }) => {
      const CHUNK = 500;
      let total = 0;
      for (let i = 0; i < input.rows.length; i += CHUNK) {
        const slice = input.rows.slice(i, i + CHUNK);
        const { data, error } = await supabase.rpc('populate_customer_import_items', {
          p_job_id: input.jobId,
          p_rows: slice as any,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        total += (data as number) ?? 0;
        log.debug(`populated chunk ${i / CHUNK + 1}: ${data} rows`);
      }
      return total;
    },
  });

  // 3) Validar em chunks (RPC v1.0.0 aceita p_chunk_size)
  const validateJob = useMutation({
    mutationFn: async (
      input:
        | string
        | {
            jobId: string;
            chunkSize?: number;
            onProgress?: (counters: ValidationCounters) => void;
          },
    ) => {
      const jobId = typeof input === 'string' ? input : input.jobId;
      const chunkSize = (typeof input === 'string' ? undefined : input.chunkSize) ?? 500;
      const onProgress = typeof input === 'string' ? undefined : input.onProgress;

      let last: ValidationCounters | null = null;
      const MAX_ITERATIONS = 500;
      for (let i = 0; i < MAX_ITERATIONS; i++) {
        const { data, error } = await supabase.rpc('validate_customer_import_job', {
          p_job_id: jobId,
          p_tenant_hint: tenantId,
          p_chunk_size: chunkSize,
        } as any);
        if (error) throw error;
        const row = (Array.isArray(data) ? data[0] : data) as ValidationCounters;
        last = row;
        const processed =
          (row?.valid_count ?? 0) +
          (row?.invalid_count ?? 0) +
          (row?.duplicate_count ?? 0) +
          (row?.blacklisted_count ?? 0);
        log.debug(`validate chunk ${i + 1}: processed=${processed}/${row?.total_rows ?? 0}`);
        try { onProgress?.(row); } catch { /* noop */ }
        if (processed >= (row?.total_rows ?? 0)) break;
      }
      if (!last) throw new Error('Validação não retornou contadores');
      return last;
    },
  });

  // 4) Aprovar e enfileirar
  const approveAndEnqueue = useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await supabase.rpc(
        'approve_and_enqueue_customer_import_job',
        {
          p_job_id: jobId,
          p_batch_size: 25,
          p_tenant_hint: tenantId,
        } as any,
      );
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return row as { job_id: string; batches_created: number; items_enqueued: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-import-jobs'] }),
  });

  // 5) Cancelar
  const cancelJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase.rpc('cancel_customer_import_job', {
        p_job_id: jobId,
        p_tenant_hint: tenantId,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-import-jobs'] }),
  });

  // 5.1) Excluir definitivamente
  const deleteJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase.rpc('delete_customer_import_job', {
        p_job_id: jobId,
        p_tenant_hint: tenantId,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-import-jobs'] }),
  });

  // 6) Detalhes (polling dinâmico: 3s ativo, 15s concluído)
  function useJobDetails(
    jobId: string | null,
    options?: { refetchInterval?: number | ((q: any) => number | false) },
  ) {
    return useQuery({
      queryKey: ['customer-import-job', jobId],
      enabled: !!jobId && !!tenantId,
      refetchInterval:
        options?.refetchInterval ??
        ((query: any) => {
          const d: any = query?.state?.data;
          const status = d?.job?.status;
          if (
            status === 'completed' ||
            status === 'completed_with_errors' ||
            status === 'failed' ||
            status === 'cancelled'
          ) {
            return 15000;
          }
          return 3000;
        }),
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_customer_import_job_details', {
          p_job_id: jobId!,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        return data as unknown as CustomerImportJobDetails;
      },
    });
  }

  // 7) Listar itens (filtro por status). Paginação real via p_offset + total_count.
  function useJobItems(
    jobId: string | null,
    statuses?: string[],
    options?: { limit?: number; offset?: number; refetchInterval?: number; enabled?: boolean },
  ) {
    const limit = options?.limit ?? 500;
    const offset = options?.offset ?? 0;
    return useQuery({
      queryKey: [
        'customer-import-items',
        tenantId,
        jobId,
        statuses?.join(',') ?? 'all',
        limit,
        offset,
      ],
      enabled: !!jobId && (options?.enabled ?? true),
      refetchInterval: options?.refetchInterval,
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_customer_import_job_items', {
          p_job_id: jobId!,
          p_statuses: statuses && statuses.length > 0 ? statuses : null,
          p_limit: limit,
          p_offset: offset,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        return ((data ?? []) as any[]) as Array<{
          id: string;
          row_index: number;
          status: string;
          raw_row: Record<string, unknown>;
          validation_errors: any;
          last_error: string | null;
          resolved_customer_id: string | null;
          total_count: number;
        }>;
      },
    });
  }

  /** Pagina todos os itens problemáticos (até 50k) — usado pelo botão "Baixar CSV de erros". */
  async function fetchAllJobItems(
    jobId: string,
    statuses: string[],
    pageSize = 1000,
  ): Promise<
    Array<{
      id: string;
      row_index: number;
      status: string;
      raw_row: Record<string, unknown>;
      validation_errors: any;
      last_error: string | null;
      resolved_customer_id: string | null;
    }>
  > {
    const all: any[] = [];
    let offset = 0;
    let total = Infinity;
    const MAX_PAGES = 50;

    for (let i = 0; i < MAX_PAGES && all.length < total; i += 1) {
      const { data, error } = await supabase.rpc('get_customer_import_job_items', {
        p_job_id: jobId,
        p_statuses: statuses.length > 0 ? statuses : null,
        p_limit: pageSize,
        p_offset: offset,
        p_tenant_hint: tenantId,
      } as any);
      if (error) throw error;
      const page = (data ?? []) as any[];
      if (page.length === 0) break;
      total = Number(page[0]?.total_count ?? page.length);
      all.push(...page);
      offset += page.length;
      if (page.length < pageSize) break;
    }
    return all;
  }

  // 8) Prévia paginada de linhas problemáticas (invalid/duplicate/blacklisted)
  function useInvalidItems(
    jobId: string | null,
    options?: { limit?: number; offset?: number; enabled?: boolean },
  ) {
    const limit = options?.limit ?? 50;
    const offset = options?.offset ?? 0;
    return useQuery({
      queryKey: ['customer-import-invalid-items', jobId, limit, offset],
      enabled: !!jobId && (options?.enabled ?? true),
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_customer_import_invalid_items', {
          p_job_id: jobId!,
          p_limit: limit,
          p_offset: offset,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        // RPC retorna colunas com prefixo out_ (RETURNS TABLE) — mapeamos
        const raw = (data ?? []) as Array<{
          out_id: string;
          out_row_index: number;
          out_status: string;
          out_raw_row: Record<string, unknown>;
          out_validation_errors: any;
          out_last_error: string | null;
          out_total_count: number;
        }>;
        const rows = raw.map((r) => ({
          id: r.out_id,
          row_index: r.out_row_index,
          status: r.out_status,
          raw_row: r.out_raw_row,
          validation_errors: r.out_validation_errors,
          last_error: r.out_last_error,
          total_count: Number(r.out_total_count ?? 0),
        }));
        return {
          items: rows,
          total: rows[0]?.total_count ?? 0,
        };
      },
    });
  }

  return {
    createJob,
    populateItems,
    validateJob,
    approveAndEnqueue,
    cancelJob,
    deleteJob,
    useJobDetails,
    useJobItems,
    fetchAllJobItems,
    useInvalidItems,
  };
}
